import { gql, useQuery } from '@apollo/client';
import type { SideNavigationLinkProps } from '@polestar/component-warehouse-react';
import { useMarket } from '@polestar/shared-front-myorder';
import { config } from '~root/src/config';

type LoggedinEnvironment = 'dev' | 'staging' | 'prod' | 'playground';
type LoggedinMenuItem = {
  href: string;
  label: {
    value: string;
  } | null;
  openInNewTab: boolean | null;
  activeInEnvironments: LoggedinEnvironment[] | null;
};

type LoggedinMenu = {
  items: LoggedinMenuItem[] | null;
};
type LoggedinMenuResponse = {
  menu: LoggedinMenu;
};

const withoutTrailingSlash = (string: string) => {
  return string.endsWith('/') ? string.slice(0, -1) : string;
};

const withActive = (item: SideNavigationLinkProps) => {
  const path = window.location.pathname;

  if (withoutTrailingSlash(item.href) === withoutTrailingSlash(path)) {
    return { ...item, active: true };
  }

  return item;
};

const parseMenu = (
  menu: LoggedinMenu,
  currentLocale: string,
  currentEnv: LoggedinEnvironment
): SideNavigationLinkProps[] => {
  const forEnv = (item: LoggedinMenuItem) => item.activeInEnvironments?.includes(currentEnv);

  const toLinkProp = (item: LoggedinMenuItem) => ({
    href: item.href.replace('{locale}', currentLocale),
    label: item?.label?.value ?? '',
    ...(item.openInNewTab ? { rel: 'noreferrer', target: '_blank' } : {})
  });

  if (!menu?.items) {
    return [];
  }

  const items =
    menu.items
      .filter(forEnv)
      .map(toLinkProp)
      .map(withActive)
      .filter((item) => item.label) ?? [];

  return items;
};

const GET_MENU_QUERY = gql`
  query GetMenu($locale: SiteLocale) {
    menu: loggedinMenu(locale: $locale, fallbackLocales: en, filter: { key: { eq: "lxp-main" } }) {
      administrativeTitle
      items: menuItems {
        administrativeTitle
        label {
          value
        }
        href
        openInNewTab
        activeInEnvironments
      }
    }
  }
`;

export const useMenuItems = () => {
  const { currentMarket } = useMarket();
  const { data, error } = useQuery<LoggedinMenuResponse>(GET_MENU_QUERY, {
    variables: {
      locale: currentMarket?.cmsLocale
    },
    context: {
      dato: true
    }
  });

  const currentEnv = config.stage;
  const menuItems = data ? parseMenu(data.menu, currentMarket?.locale ?? '', currentEnv) : [];

  return { data, error, menuItems };
};
